export interface SupportInquiry {
  id: string;
  email: string;
  inquiry_type: string;
  /** 어느 앱의 문의인지(admin 앱 구분 id). 서버가 컬럼 이전 행은 sajug 로 돌려준다. */
  app: string;
  content: string;
  status: string;
  reply_content: string | null;
  replied_at: string | null;
  created_at: string;
  updated_at?: string | null;
  attachments: SupportAttachment[];
  reply_attachments: SupportAttachment[];
}

export interface SupportAttachment {
  id: string;
  inquiry_id: string;
  original_name: string;
  content_type: string;
  byte_size: number;
  url: string;
  created_at?: string | null;
}

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const getSupportInquiryCreatedTime = (inquiry: SupportInquiry): number => {
  const parsed = new Date(inquiry.created_at);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : 0;
};

export const normalizeSupportInquiries = (payload: unknown): SupportInquiry[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item): SupportInquiry | null => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const record = item as Record<string, unknown>;
      const id = asString(record['id']);
      const email = asString(record['email']);
      const inquiryType = asString(record['inquiry_type'] ?? record['inquiryType']);
      const app = asString(record['app']) ?? 'sajug';
      const content = asString(record['content']);
      const status = asString(record['status']);
      const createdAt = asString(record['created_at'] ?? record['createdAt']);
      if (!id || !email || !inquiryType || !content || !status || !createdAt) {
        return null;
      }

      const normalizeAttachments = (value: unknown): SupportAttachment[] => Array.isArray(value)
        ? value.flatMap((rawAttachment) => {
            if (!rawAttachment || typeof rawAttachment !== 'object') return [];
            const attachment = rawAttachment as Record<string, unknown>;
            const attachmentId = asString(attachment['id']);
            const inquiryId = asString(attachment['inquiry_id'] ?? attachment['inquiryId']);
            const originalName = asString(attachment['original_name'] ?? attachment['originalName']);
            const contentType = asString(attachment['content_type'] ?? attachment['contentType']);
            const byteSize = Number(attachment['byte_size'] ?? attachment['byteSize']);
            const url = asString(attachment['url']);
            if (!attachmentId || !inquiryId || !originalName || !contentType || !Number.isFinite(byteSize) || !url) return [];
            return [{
              id: attachmentId,
              inquiry_id: inquiryId,
              original_name: originalName,
              content_type: contentType,
              byte_size: byteSize,
              url,
              created_at: asString(attachment['created_at'] ?? attachment['createdAt']),
            }];
          })
        : [];

      return {
        id,
        email,
        inquiry_type: inquiryType,
        app,
        content,
        status,
        reply_content: asString(record['reply_content'] ?? record['replyContent']),
        replied_at: asString(record['replied_at'] ?? record['repliedAt']),
        created_at: createdAt,
        updated_at: asString(record['updated_at'] ?? record['updatedAt']),
        attachments: normalizeAttachments(record['attachments']),
        reply_attachments: normalizeAttachments(record['reply_attachments'] ?? record['replyAttachments']),
      };
    })
    .filter((item): item is SupportInquiry => item !== null);
};

export const sortSupportInquiriesByCreatedDesc = (inquiries: SupportInquiry[]): SupportInquiry[] => {
  return [...inquiries].sort((left, right) => getSupportInquiryCreatedTime(right) - getSupportInquiryCreatedTime(left));
};

export const countPendingSupportInquiries = (inquiries: SupportInquiry[]): number => {
  return inquiries.filter((inquiry) => inquiry.status.toLowerCase() === 'pending').length;
};
